import { FC, useEffect, useRef, useState } from "react";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import {
  beefTabsStyles,
  processAndStageStylesV2,
} from "../../../../../../_metronic/layout";
import { useFormContext } from "react-hook-form";
import {
  CompoundFeedDatabase,
  MasterDataOption,
  Maybe,
  StageType,
} from "../../../../../../graphql/types";
import { cloneDeep } from "lodash";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";

import { Origin } from "../types";
import {
  BeefBaseline,
  BeefBreedingFeed,
  BeefBreedingStageData,
  BeefGrowingStageData,
} from "../../../../models/Baseline/BeefBaseline";
import {
  BeefBreedingStageDataIntervention,
  BeefGrowingStageDataIntervention,
  BeefIntervention,
} from "../../../../models/Intervention/BeefIntervention";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import BeefFeedDatabaseInfo from "./BeefFeedDatabaseInfo";
import { Box, Tab, Tabs } from "@material-ui/core";
import TabPanel from "../../../helpers/TabPanel";
import BeefFeedDietCharacterisation from "./BeefFeedDietCharacterisation";
import {
  CattleFeed as CattleBaselineFeed,
  TargetAnimal,
} from "../../../../models/Baseline/CattleBaseline";
import BeefFeedSingleIngredient from "./BeefFeedSingleIngredient";
import { Spacer } from "../../../helpers/Spacer";
import BeefFeedCompoundFeed from "./BeefFeedCompoundFeed";
import BeefFeedMothersMilk from "./BeefFeedMothersMilk";
import BeefFeedFreshGrass from "./BeedFeedFreshGrass";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";

interface BeefFeedDialogProps {
  compoundFeeds?: Maybe<Array<MasterDataOption>>;
  singleIngredients: Maybe<Array<MasterDataOption>>;
  origins: Maybe<Array<Origin>>;
  formType: FormType;
  stageIndex: number;
  stageType: StageType;
  baseline: BeefBaseline;
  handleCancel: (param?: string | null) => void;
}

type BeefBreedingFeedIntervention = {
  cows: CattleFeed;
  calves: CattleFeed;
};

const BeefFeedDialog: FC<BeefFeedDialogProps> = ({
  compoundFeeds,
  singleIngredients,
  origins,
  formType = FormType.Add,
  stageIndex = 0,
  stageType,
  baseline,
  handleCancel,
}) => {
  const classes: CSSClassesList = processAndStageStylesV2();
  const tabsStyles = beefTabsStyles();
  const intl = useIntl();
  const currResetValue = useRef<CattleFeed | BeefBreedingFeedIntervention>();
  const fc = useFormContext<BeefIntervention>();
  const [currentTab, setCurrentTab] = useState(0);

  const [growingEditStates, setGrowingEditStates] = useState({
    isDietEdit: false,
    isSingleIngredientEdit: false,
    isCompoundFeedEdit: false,
    isMotherMilkEdit: false,
    isFreshGrassEdit: false,
  });

  const [breedingEditStates, setBreedingEditStates] = useState({
    [TargetAnimal.COWS]: {
      isDietEdit: false,
      isSingleIngredientEdit: false,
      isCompoundFeedEdit: false,
      isMotherMilkEdit: false,
      isFreshGrassEdit: false,
    },
    [TargetAnimal.CALVES]: {
      isDietEdit: false,
      isSingleIngredientEdit: false,
      isCompoundFeedEdit: false,
      isMotherMilkEdit: false,
      isFreshGrassEdit: false,
    },
  });

  const formTitle = intl.formatMessage({
    id: "SUSTELL.PROCESS.DIALOG.STAGE.FEED.TITLE",
  });

  const formIntroText = intl.formatMessage({
    id: "SUSTELL.STAGE.DAIRY.FEED.CONTINUOUS_GROWING.DESCRIPTION",
  });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.feed`;

  const currentStageData = baseline.stages[stageIndex].stageData;

  let beedFeedData: CattleBaselineFeed | BeefBreedingFeed;
  switch (stageType) {
    case StageType.Growing:
      beedFeedData = (currentStageData as BeefGrowingStageData).feed;
      break;

    case StageType.Breeding:
      beedFeedData = (currentStageData as BeefBreedingStageData).feed;
      break;

    default:
      beedFeedData = {} as CattleBaselineFeed;
      break;
  }

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const setEditState = (
    key: keyof typeof growingEditStates,
    value: boolean,
    target?: TargetAnimal
  ) => {
    if (stageType === StageType.Growing) {
      setGrowingEditStates((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (stageType === StageType.Breeding && target) {
      setBreedingEditStates((prev) => ({
        ...prev,
        [target]: {
          ...prev[target],
          [key]: value,
        },
      }));
    }
  };

  const handleDialogCancelClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as BeefIntervention;

      const feedReset = resetObject.stages[stageIndex]?.stageData as
        | BeefGrowingStageDataIntervention
        | BeefBreedingStageDataIntervention;

      if (feedReset?.feed) {
        feedReset.feed = currResetValue.current;
        fc.reset(resetObject, { errors: true });
      }
    } else {
      fc.reset(undefined, { errors: true });
    }

    if (handleCancel) {
      handleCancel();
    }
  };

  const getFieldItemPrefix = (target?: TargetAnimal) => {
    return target ? `${fieldItemPrefix}.${target}` : fieldItemPrefix;
  };

  const getCurrentFormValues = (target: TargetAnimal | undefined) =>
    target
      ? (currResetValue.current as BeefBreedingFeedIntervention)?.[target]
      : (currResetValue.current as CattleFeed);

  const getCurrentStageData = (target?: TargetAnimal) =>
    target
      ? (beedFeedData as BeefBreedingFeed)[target as keyof BeefBreedingFeed]
      : (beedFeedData as CattleBaselineFeed);

  const renderContent = (target?: TargetAnimal) => {
    const fieldItemPrefix = getFieldItemPrefix(target);
    const currentStageData = getCurrentStageData(target);
    const currentFormValues = getCurrentFormValues(target);

    const editStates =
      stageType === StageType.Growing
        ? growingEditStates
        : breedingEditStates[target ?? TargetAnimal.COWS];

    return (
      <>
        <DsmGrid
          className={classes.dsmGridThreeColumnSecondBigOthersSmall}
          style={{ grid: "auto/0.35fr 1fr" }}
        >
          <BeefFeedDatabaseInfo
            databaseFoundation={baseline?.info.databaseFoundation}
            reportingType={baseline?.info.reportingType}
          />
          <BeefFeedDietCharacterisation
            fieldItemPrefix={fieldItemPrefix}
            stageType={stageType}
            formType={formType}
            isDietEdit={editStates.isDietEdit}
            setIsDietEdit={(val) => setEditState("isDietEdit", val, target)}
            target={target}
            baselineStageData={currentStageData}
            currentFormValues={currentFormValues}
          />
        </DsmGrid>

        <Spacer />

        <DsmGrid className={classes.dsmGridTwoColumn2To3}>
          <BeefFeedCompoundFeed
            databaseFoundation={
              baseline?.info?.databaseFoundation || CompoundFeedDatabase.Gfli
            }
            compoundFeeds={compoundFeeds}
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            baselineStageData={currentStageData}
            currentFormValues={currentFormValues}
            isCompoundFeedEdit={editStates.isCompoundFeedEdit}
            setIsCompFeedEdit={(val) =>
              setEditState("isCompoundFeedEdit", val, target)
            }
          />
          <BeefFeedSingleIngredient
            singleIngredients={singleIngredients}
            origins={origins}
            formType={formType}
            stageIndex={stageIndex}
            fieldItemPrefix={fieldItemPrefix}
            baselineStageData={currentStageData}
            currentFormValues={currentFormValues}
            isSingleIngredientEdit={editStates.isSingleIngredientEdit}
            setIsSingleIngEdit={(val) =>
              setEditState("isSingleIngredientEdit", val, target)
            }
          />
        </DsmGrid>

        {target === TargetAnimal.CALVES && (
          <>
            <Spacer height="16px" />
            <BeefFeedMothersMilk
              formType={formType}
              fieldItemPrefix={fieldItemPrefix}
              baselineStageData={currentStageData}
              currentFormValues={currentFormValues}
              isMotherMilkEdit={editStates.isMotherMilkEdit}
              setIsMotherMilkEdit={(val) =>
                setEditState("isMotherMilkEdit", val, target)
              }
            />
          </>
        )}

        <>
          <Spacer />
          <BeefFeedFreshGrass
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            baselineStageData={currentStageData}
            currentFormValues={currentFormValues}
            target={target}
            isFreshGrassEdit={editStates.isFreshGrassEdit}
            setIsFreshGrassEdit={(val) =>
              setEditState("isFreshGrassEdit", val, target)
            }
          />
        </>
      </>
    );
  };

  const renderBreedingContent = () => (
    <>
      <Box className={tabsStyles.tabsBox}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="beef-breeding-emissions"
          className={tabsStyles.tabs}
        >
          <Tab
            label={intl.formatMessage({
              id: `SUSTELL.STAGE.BEEF.STEPS.COWS`,
            })}
          />
          <Tab
            label={intl.formatMessage({
              id: `SUSTELL.STAGE.BEEF.STEPS.CALVES`,
            })}
          />
        </Tabs>
      </Box>
      <TabPanel value={currentTab} index={0}>
        {renderContent(TargetAnimal.COWS)}
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        {renderContent(TargetAnimal.CALVES)}
      </TabPanel>
    </>
  );

  const renderGrowingContent = () => renderContent();

  return (
    <DialogContainer
      formVisible
      handleClose={handleDialogCancelClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      introText={formIntroText}
      datasetType="intervention"
      variant="ultrawide"
    >
      {stageType === StageType.Growing && renderGrowingContent()}
      {stageType === StageType.Breeding && renderBreedingContent()}

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={() => { handleCancel() }}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  );
};

BeefFeedDialog.defaultProps = {
  compoundFeeds: [],
};

export default BeefFeedDialog;
